import { ASSISTANT_VOICE_SECONDS_MAX, assistantVoiceMimeTypes, type AssistantVoiceMimeType } from "@teamna/shared";
import { useEffect, useRef, useState } from "react";
import { copyForError } from "@teamna/web-client/api/copy.ts";
import { useAssistantTranscribe } from "@teamna/web-client/hooks/use-assistant.ts";
import { MicIcon, Spinner, StopIcon } from "../../components/index.ts";

/**
 * The microphone of the AI Assistant composer (strategy repo `teamna/strategy/ai-assistant-v1.md`,
 * item 7 "Voice", built 2026-10-23). Tap to record, tap again to stop (it stops by itself after 60
 * seconds). The API turns the recording into text, and the text goes into the compose box: the
 * person reads it and taps Send, so a wrong word never goes to AI Assistant unseen.
 *
 * A browser with no recorder (or no microphone access in the page) shows no button.
 */

/** The recorder formats to ask for, best first. Chrome and Firefox take WebM or Ogg, Safari MP4. */
const RECORDER_TYPES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus", "audio/ogg"];

/** The first format this browser records, or "" to let it choose. Exported for the tests. */
export function pickRecorderType(isTypeSupported: (type: string) => boolean): string {
  return RECORDER_TYPES.find((type) => isTypeSupported(type)) ?? "";
}

/** "audio/webm;codecs=opus" becomes "audio/webm". Null for a format the API does not take. */
export function voiceMimeType(blobType: string): AssistantVoiceMimeType | null {
  const base = blobType.split(";")[0]!.trim().toLowerCase();
  return (assistantVoiceMimeTypes as readonly string[]).includes(base) ? (base as AssistantVoiceMimeType) : null;
}

/** 0:07, 1:00. */
export function formatRecordingTime(seconds: number): string {
  const whole = Math.floor(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

async function toBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  // In parts: one String.fromCharCode call with a million arguments overflows the stack.
  for (let at = 0; at < bytes.length; at += 0x8000) binary += String.fromCharCode(...bytes.subarray(at, at + 0x8000));
  return btoa(binary);
}

function canRecord(): boolean {
  return typeof window !== "undefined" && typeof MediaRecorder !== "undefined" && typeof navigator.mediaDevices?.getUserMedia === "function";
}

type Recording = { recorder: MediaRecorder; stream: MediaStream; startedAt: number; timer: ReturnType<typeof setInterval> };

export function VoiceButton({
  businessId,
  disabled = false,
  onText,
}: {
  businessId: string;
  disabled?: boolean;
  /** The text of the recording, never empty. */
  onText: (text: string) => void;
}) {
  const transcribe = useAssistantTranscribe(businessId);
  const [supported] = useState(canRecord);
  const [seconds, setSeconds] = useState<number | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  // The browser asks for the microphone: one start at a time (review F1.2). The ref guards two taps
  // in one tick; the state disables the button.
  const [starting, setStarting] = useState(false);
  const startingNow = useRef(false);
  const recording = useRef<Recording | null>(null);
  const mounted = useRef(true);

  // Leaving the screen while it records: stop the microphone, send nothing.
  useEffect(() => {
    // Set again on each setup: StrictMode runs setup, cleanup, setup in development (review F2.2).
    mounted.current = true;
    return () => {
      mounted.current = false;
      const current = recording.current;
      if (!current) return;
      recording.current = null;
      clearInterval(current.timer);
      current.recorder.ondataavailable = null;
      current.recorder.onstop = null;
      if (current.recorder.state !== "inactive") current.recorder.stop();
      for (const track of current.stream.getTracks()) track.stop();
    };
  }, []);

  if (!supported) return null;

  async function start() {
    if (startingNow.current || recording.current) return;
    startingNow.current = true;
    setProblem(null);
    transcribe.reset();
    setStarting(true);
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      startingNow.current = false;
      if (!mounted.current) return;
      setStarting(false);
      setProblem(
        error instanceof DOMException && (error.name === "NotAllowedError" || error.name === "SecurityError")
          ? "Allow the microphone for this site in your browser settings, then try again."
          : "Teamna cannot use a microphone on this device.",
      );
      return;
    }
    // The screen closed while the browser asked: give the microphone back at once.
    startingNow.current = false;
    if (!mounted.current) {
      for (const track of stream.getTracks()) track.stop();
      return;
    }
    setStarting(false);
    const type = pickRecorderType((candidate) => MediaRecorder.isTypeSupported(candidate));
    const recorder = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
    const chunks: Blob[] = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    };
    recorder.onstop = () => void finish(new Blob(chunks, { type: recorder.mimeType || type }));
    const startedAt = Date.now();
    const timer = setInterval(() => {
      const elapsed = (Date.now() - startedAt) / 1000;
      setSeconds(elapsed);
      if (elapsed >= ASSISTANT_VOICE_SECONDS_MAX) stop();
    }, 250);
    recording.current = { recorder, stream, startedAt, timer };
    setSeconds(0);
    recorder.start();
  }

  function stop() {
    const current = recording.current;
    if (!current || current.recorder.state === "inactive") return;
    clearInterval(current.timer);
    current.recorder.stop();
    for (const track of current.stream.getTracks()) track.stop();
  }

  async function finish(blob: Blob) {
    const current = recording.current;
    recording.current = null;
    setSeconds(null);
    const length = current ? Math.min((Date.now() - current.startedAt) / 1000, ASSISTANT_VOICE_SECONDS_MAX) : 0;
    const mimeType = voiceMimeType(blob.type);
    if (!mimeType) return setProblem("This browser records in a format Teamna cannot read. Type your question.");
    if (blob.size === 0 || length < 0.5) return setProblem("The recording is too short. Record for at least one second, then tap Stop.");
    transcribe.mutate(
      { audio: await toBase64(blob), mimeType, seconds: length },
      {
        onSuccess: ({ text }) => (text ? onText(text) : setProblem("We heard no words. Try again, closer to the microphone.")),
      },
    );
  }

  const listening = seconds !== null;
  const working = transcribe.isPending;
  const error = problem ?? (transcribe.error ? copyForError(transcribe.error).message : null);
  let label = "Ask with your voice";
  if (listening) label = "Stop recording";
  else if (working) label = "Turning your voice into text";

  return (
    <div className="relative shrink-0">
      {listening ? (
        <span aria-hidden="true" className="absolute -top-7 left-1/2 -translate-x-1/2 rounded-pill bg-cancelled px-2 py-0.5 text-[12px] font-semibold text-inverse tabular-nums">
          {formatRecordingTime(seconds)}
        </span>
      ) : null}
      <button
        type="button"
        aria-label={label}
        aria-pressed={listening}
        title={label}
        disabled={(disabled && !listening) || working || starting}
        onClick={() => (listening ? stop() : void start())}
        className={[
          "relative flex size-11 items-center justify-center rounded-full border transition-colors disabled:opacity-40",
          listening ? "border-cancelled bg-cancelled text-inverse" : "border-line bg-surface text-ink-2 hover:border-accent-soft hover:text-ink",
        ].join(" ")}
      >
        {listening ? <span aria-hidden="true" className="assistant-rec" /> : null}
        {working ? <Spinner /> : listening ? <StopIcon size={18} /> : <MicIcon size={20} />}
      </button>
      <output className="sr-only">{listening ? "Recording. Tap stop when you finish." : working ? "Turning your voice into text." : ""}</output>
      {error ? (
        <p role="alert" className="absolute right-0 bottom-full mb-8 w-64 rounded-lg border border-line bg-paper px-3 py-2 text-[12.5px] text-cancelled shadow-card">
          {error}
        </p>
      ) : null}
    </div>
  );
}
